import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import type { ColumnSizingState } from '@tanstack/react-table';
import { getColumnSizing } from './columnDefinitions';
import { useRafResizeObserver } from '../../hooks/useRafResizeObserver';
import type { TableColumn } from './types';

type Bounds = { minWidth: number; maxWidth: number; width: number };
const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

/** Distribute only as much as the remaining bounds permit. Mutates a fresh local width array. */
function distribute(widths: number[], indexes: number[], delta: number, bounds: Bounds[]) {
  const capacities = indexes.map((index) =>
    Math.max(0, delta > 0 ? bounds[index].maxWidth - widths[index] : widths[index] - bounds[index].minWidth),
  );
  const capacity = sum(capacities);
  if (capacity === 0) return;
  const amount = Math.min(Math.abs(delta), capacity) * Math.sign(delta);
  indexes.forEach((index, position) => {
    widths[index] += (amount * capacities[position]) / capacity;
  });
}

function getBounds(columns: TableColumn[], available: number, scrollEnabled: boolean): Bounds[] {
  const bounds = columns.map(getColumnSizing);
  if (scrollEnabled || available <= 0 || !bounds.length) return bounds;
  const minimum = sum(bounds.map((bound) => bound.minWidth));
  if (minimum > available)
    bounds.forEach((bound) => {
      bound.minWidth = (bound.minWidth * available) / minimum;
    });
  // Fit mode must fill even when all declared maxima together are too small
  // (e.g. a single metric column). Only the last column relaxes its maximum.
  const shortage = available - sum(bounds.map((bound) => bound.maxWidth));
  if (shortage > 0) bounds[bounds.length - 1].maxWidth += shortage;
  return bounds;
}

type Drag = {
  index: number;
  ids: string[];
  widths: number[];
  bounds: Bounds[];
  start: number;
  pointerX: number;
  scrollEnabled: boolean;
  originalSizing: ColumnSizingState;
  element: HTMLDivElement;
  pointerId: number;
};

export function useColumnSizing(
  columns: TableColumn[],
  scrollEnabled: boolean,
  enabled: boolean,
  onCommit?: (widths: ColumnSizingState) => void,
) {
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(0);
  const [sizing, setSizing] = useState<ColumnSizingState>({});
  const [resizingId, setResizingId] = useState<string | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const frameRef = useRef<number>();
  const definitionKey = JSON.stringify(
    columns.map(({ id, width, minWidth, maxWidth }) => [id, width, minWidth, maxWidth]),
  );
  const previousDefinitions = useRef(new Map<string, string>());

  const measureWidth = useCallback((element: HTMLDivElement) => {
    setAvailable(element.clientWidth);
  }, []);
  useRafResizeObserver(scrollerRef, measureWidth);

  useEffect(() => {
    const definitions = new Map(
      columns.map((column) => [column.id, JSON.stringify([column.width, column.minWidth, column.maxWidth])]),
    );
    const previousValues = previousDefinitions.current;
    setSizing((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(
          ([id]) => definitions.has(id) && definitions.get(id) === previousValues.get(id),
        ),
      ),
    );
    previousDefinitions.current = definitions;
  }, [columns, definitionKey]);

  const bounds = useMemo(() => getBounds(columns, available, scrollEnabled), [columns, available, scrollEnabled]);
  const widths = useMemo(() => {
    const values = columns.map((column, index) =>
      clamp(sizing[column.id] ?? bounds[index].width, bounds[index].minWidth, bounds[index].maxWidth),
    );
    if (available <= 0) return values;
    const delta = available - sum(values);
    if (!scrollEnabled && values.length) {
      const last = values.length - 1;
      // Preserve earlier widths where possible. The final column absorbs space
      // on initial layout, viewport changes and column removal/addition.
      values[last] = clamp(values[last] + delta, bounds[last].minWidth, bounds[last].maxWidth);
      distribute(
        values,
        values.map((_, index) => index),
        available - sum(values),
        bounds,
      );
      // Remove floating-point residue so the table and its columns share one width.
      values[last] += available - sum(values);
    } else if (delta > 0) {
      // Explicit/user-sized columns keep their pixel width; flexible columns absorb spare space.
      const flexible = columns.flatMap((column, index) =>
        column.width === undefined && sizing[column.id] === undefined ? [index] : [],
      );
      distribute(values, flexible, delta, bounds);
    }
    return values;
  }, [columns, sizing, bounds, available, scrollEnabled]);

  const applyDrag = useCallback(() => {
    const drag = dragRef.current;
    if (!drag) return;
    // Use viewport deltas. Native scrollLeft can clamp as a table shrinks at the
    // right edge; feeding that offset back into sizing would keep shrinking it.
    const delta = drag.pointerX - drag.start;
    const next = [...drag.widths];
    const target = clamp(next[drag.index] + delta, drag.bounds[drag.index].minWidth, drag.bounds[drag.index].maxWidth);
    const change = target - next[drag.index];
    next[drag.index] = target;
    if (!drag.scrollEnabled) {
      // Transfer space only to/from right-hand neighbours. Stop at either side's
      // bounds instead of allowing shrinking to leave whitespace at the end.
      let remaining = Math.abs(change);
      for (let index = drag.index + 1; index < next.length && remaining > 0; index++) {
        const capacity =
          change > 0 ? next[index] - drag.bounds[index].minWidth : drag.bounds[index].maxWidth - next[index];
        const amount = Math.min(remaining, Math.max(0, capacity));
        next[index] += change > 0 ? -amount : amount;
        remaining -= amount;
      }
      next[drag.index] -= Math.sign(change) * remaining;
    }
    const nextSizing = Object.fromEntries(drag.ids.map((id, index) => [id, next[index]]));
    setSizing(nextSizing);
    return nextSizing;
  }, []);

  const scheduleDrag = useCallback(() => {
    if (frameRef.current !== undefined) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined;
      applyDrag();
    });
  }, [applyDrag]);

  const finishDrag = useCallback(
    (cancel = false) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
      frameRef.current = undefined;
      if (cancel) setSizing(drag.originalSizing);
      else {
        const committed = applyDrag();
        if (committed) commitRef.current?.(committed);
      }
      dragRef.current = null;
      if (drag.element.hasPointerCapture(drag.pointerId)) drag.element.releasePointerCapture(drag.pointerId);
      setResizingId(null);
    },
    [applyDrag],
  );

  useEffect(() => {
    finishDrag(true);
  }, [definitionKey, scrollEnabled, enabled, available, finishDrag]);
  useEffect(
    () => () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
      dragRef.current = null;
    },
    [],
  );

  const getResizeHandlers = (index: number) => ({
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      if (
        !enabled ||
        (!scrollEnabled && index === columns.length - 1) ||
        !event.isPrimary ||
        event.button !== 0 ||
        available <= 0
      )
        return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        index,
        ids: columns.map((column) => column.id),
        widths,
        bounds,
        start: event.clientX,
        pointerX: event.clientX,
        scrollEnabled,
        originalSizing: sizing,
        element: event.currentTarget,
        pointerId: event.pointerId,
      };
      setResizingId(columns[index].id);
    },
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      if (dragRef.current?.pointerId !== event.pointerId) return;
      dragRef.current.pointerX = event.clientX;
      scheduleDrag();
    },
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => {
      if (dragRef.current?.pointerId !== event.pointerId) return;
      dragRef.current.pointerX = event.clientX;
      finishDrag();
    },
    onPointerCancel: () => finishDrag(true),
    onLostPointerCapture: () => finishDrag(true),
    onDoubleClick: () =>
      setSizing((previous) => {
        const next = { ...previous };
        delete next[columns[index].id];
        return next;
      }),
  });

  return {
    scrollerRef,
    bounds,
    widths,
    resizingId,
    getResizeHandlers,
    columnSizing: Object.fromEntries(columns.map((column, index) => [column.id, widths[index]])),
    onColumnSizingChange: setSizing,
  };
}
